import {Fragment, useEffect, useState, useMemo, useCallback, useRef} from "react";
import { useScrollbarSync } from "../../hooks/useScrollbarSync";
import { usePaginatedSearch } from "../../hooks/usePaginatedSearch";
import {CircularProgress, TableCell, TableRow, Grid, Paper, Table, TableBody, makeStyles} from "@material-ui/core";
import ArtApi from "../../api/ArtApi";
import Button from '@material-ui/core/Button';
import { Icon } from '@material-ui/core';
import { convertTimeToExcelTime, formatUTC } from "../../lib/helpers";
import { buildCsvRow } from "../../lib/csv";
import { EditableCell, EditableDateCell } from "../../components/EditableFields";
import { MultiSelectFilter } from "../../components/MultiSelectFilter";
import { SearchBar } from "../../components/SearchBar";
import ErrorSnackbar from "../../components/ErrorSnackbar";

const useStyles = makeStyles({
    checkboxContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        margin: 0,
    },
    checkbox: {
        margin: '0 5px 0 0',
    },
    scrollWrapper: {
        width: '100%',
    },
    topScrollbar: {
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        backgroundColor: '#424242',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
    },
    topScrollbarInner: {
        height: '1px',
    },
    tableContainer: {
        width: '100%',
        overflowX: 'auto',
        borderTopLeftRadius: '0 !important',
        borderTopRightRadius: '0 !important',
        /* Hide scrollbar for Chrome, Safari and Opera */
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        /* Hide scrollbar for IE, Edge and Firefox */
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
    },
    table: {
        tableLayout: 'auto',
        width: '100%',
        '& td, & th': {
            fontSize: 'clamp(0.65rem, 1.2vw, 0.75rem)',
            padding: '8px 6px',
            verticalAlign: 'middle',
        },
    },
    compactCell: {
        maxWidth: '120px',
        wordBreak: 'break-word',
    },
    wrapCell: {
        maxWidth: '120px',
        width: '120px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        '& input, & textarea': {
            maxWidth: '100%',
            width: '100%',
            minWidth: 'auto',
            boxSizing: 'border-box',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
        }
    },
    statusExplanationCell: {
        minWidth: '380px',
        width: '380px',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        padding: '14px 6px !important',
        '& input, & textarea': {
            maxWidth: '100%',
            width: '100%',
            minWidth: 'auto',
            minHeight: '72px !important',
            lineHeight: '1.4',
            boxSizing: 'border-box',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
        }
    },
    dateCell: {
        fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
        whiteSpace: 'nowrap',
    },
    actionCell: {
        padding: '6px 4px !important',
    },
    compactButton: {
        fontSize: 'clamp(0.6rem, 1vw, 0.7rem)',
        padding: '2px 6px',
        minWidth: 'auto',
        margin: '2px !important',
    }
});


interface RheemEventCandidatesProps {
    coreId?: string
    staleAt?: Date | null
}


export const RheemEventCandidates = (props: RheemEventCandidatesProps) => {
    const classes = useStyles();
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [eventCandidates, setEventCandidates] = useState<any | null>(null);
    const [eventTypes, setEventTypes] = useState<string[]>([]);
    const [isExporting, setIsExporting] = useState(false);

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sort, setSort] = useState("");
    const [filters, setFilters] = useState<Record<string, string[]>>({
        eventType: [],
        mlDecision: []
    });
    const [includeArchived, setIncludeArchived] = useState(false);

    // ML Decision filter options (hardcoded as they're fixed values)
    const mlDecisionOptions = ['LIKELY_TP', 'POTENTIAL_FP', 'UNCERTAIN'];
    const mountedRef = useRef(true);
    const { topScrollRef, bottomScrollRef, topScrollInnerRef } = useScrollbarSync(eventCandidates);

    // Column headers mapped to their data keys
    const eventCandidatesTableColumns: Record<string, string> = {
        "Event Type": "eventType",
        "Event SubType": "eventSubType",
        "Event Description": "eventDescription",
        "MAC Address": "mac",
        "Core ID": "coreID",
        "Start Time": "eventStart",
        "End Time": "eventEnd",
        "Duration": "duration",
        "Suspected Real Start": "suspectedRealStart",
        "Suspected Real End": "suspectedRealEnd",
        "Start Time (excel)": "eventStartExcel",
        "End Time (excel)": "eventEndExcel",
        "Duration (excel)": "durationExcel",
        "Suspected Real Start (excel)": "suspectedRealStartExcel",
        "Suspected Real End (excel)": "suspectedRealEndExcel",
        "Suspected Duration (excel)": "suspectedDurationExcel",
        "Start Status": "startStatus",
        "End Status": "endStatus",
        "Status Explanation": "statusExplanation",
        "No Penalty For Missing": "noPenaltyForMissing",
        "Archived": "archived",
        "DeleteEvent": "deleteEvent"
    };

    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);

    function getEventCandidates() {
        setIsLoading(true);
        ArtApi.getRheemEventCandidates(sort, page, filters.eventType, 25, props.coreId, committedSearchTerm, includeArchived)
            .then(response => {
                if (!mountedRef.current) return;
                setIsLoading(false);
                if (response.error) {
                    setError(response.error);
                    setSnackbarOpen(true);
                }

                let { total, page, events, pageSize, types } = response.response;
                setTotal(total);
                setTotalPages(Math.ceil(total / pageSize));
                setPage(page);
                setEventCandidates(events);
                setEventTypes(types);
            }).catch(error => {
                if (!mountedRef.current) return;
                setIsLoading(false);
                setError(error.message);
                setSnackbarOpen(true);
            })
    }

    const { searchTerm, setSearchTerm, committedSearchTerm, doSearch, clearSearch } = usePaginatedSearch({
        page, setPage, load: getEventCandidates
    });

    useEffect(() => {
        getEventCandidates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, sort, filters.eventType, props.coreId, props.staleAt, includeArchived, committedSearchTerm]);

    // Client-side filtering for ml_decision (since backend doesn't support it yet)
    const filteredEventCandidates = useMemo(() => {
        if (!eventCandidates) return null;

        const mlDecisionExcludes = filters.mlDecision || [];
        if (mlDecisionExcludes.length === 0) {
            return eventCandidates;
        }

        return eventCandidates.filter((event: any) => {
            const mlDecision = event.additionalInfo?.ml_decision;
            if (!mlDecision) return true; // Include events without ml_decision
            return !mlDecisionExcludes.includes(mlDecision);
        });
    }, [eventCandidates, filters.mlDecision]);

    const renderActionButtons = useCallback((eventId, statusType, value) => {

        const handleToggle = (event: any, newState: string) => {
            if(value === newState) {
                newState = "";
            }

            // Optimistically update UI
            setEventCandidates((prev: any) => prev.map((e: any) =>
                e.id === eventId ? { ...e, [statusType]: newState } : e
            ));

            // Sync with backend and refetch to get authoritative state
            ArtApi.patchRheemEventCandidate(eventId, { [statusType]: newState })
                .then(() => {
                    // Refetch to ensure UI matches server state
                    getEventCandidates();
                })
                .catch(() => {
                    // Revert on error
                    getEventCandidates();
                });
        }

        const isCommitted = value === "COMMITTED";

        return (
            <TableCell className={classes.actionCell}>
                {isCommitted && (
                    <span style={{ color: "green", fontSize: '0.6rem', display: 'block' }}>Committed</span>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Button
                        variant="contained"
                        onClick={(event) => handleToggle(event, "APPROVED")}
                        className={classes.compactButton}
                        style={{ backgroundColor: value === 'APPROVED' ? 'lightgreen' : undefined, fontWeight: value === 'APPROVED' ? 'bold' : undefined }}
                        title="Approve">
                        ✓
                    </Button>

                    <Button
                        variant="contained"
                        onClick={(event) => handleToggle(event, "REJECTED")}
                        className={classes.compactButton}
                        style={{ backgroundColor: value === 'REJECTED' ? 'red' : undefined, fontWeight: value === 'REJECTED' ? 'bold' : undefined }}
                        title="Reject">
                        ✗
                    </Button>

                    <Button
                        variant="contained"
                        onClick={(event) => handleToggle(event, "UNSURE")}
                        className={classes.compactButton}
                        style={{ backgroundColor: value === 'UNSURE' ? 'lightyellow' : undefined, fontWeight: value === 'UNSURE' ? 'bold' : undefined }}
                        title="Unsure">
                        ?
                    </Button>
                </div>
            </TableCell>
        )
    }, [getEventCandidates, setEventCandidates, classes]);

    const renderDeleteButton = useCallback((eventId) => {
        // Render a button that deletes the specified event by ID
        const handleDelete = (event: any) => {
            if (!window.confirm("Are you sure you want to delete this event?")) {
                return;
            }
            ArtApi.deleteRheemEventCandidate(eventId).then(() => {
                if (mountedRef.current) getEventCandidates();
            });
        }
        return (
            <TableCell className={classes.actionCell}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDelete}
                    className={classes.compactButton}
                    title="Delete">
                    <Icon style={{ fontSize: '0.9rem' }}>delete</Icon>
                </Button>
            </TableCell>
        )
    }, [getEventCandidates, mountedRef, classes]);

    const renderArchiveButton = useCallback((eventId, isArchived) => {
        const handleArchive = (event: any) => {
            const newArchivedState = !isArchived;
            ArtApi.patchRheemEventCandidate(eventId, { archived: newArchivedState }).then(() => {
                if (mountedRef.current) getEventCandidates();
            });
        }

        return (
            <TableCell className={classes.actionCell}>
                <Button
                    variant="contained"
                    onClick={handleArchive}
                    className={classes.compactButton}
                    style={{
                        backgroundColor: isArchived ? 'orange' : undefined,
                        fontWeight: isArchived ? 'bold' : undefined
                    }}
                    title={isArchived ? "Unarchive" : "Archive"}
                >
                    <Icon style={{ fontSize: '0.9rem' }}>{isArchived ? "unarchive" : "archive"}</Icon>
                </Button>
            </TableCell>
        )
    }, [getEventCandidates, mountedRef, classes]);

    const renderNoPenaltyCheckbox = useCallback((eventId, noPenaltyForMissing) => {
        const handleToggle = (event: any) => {
            ArtApi.patchRheemEventCandidate(eventId, { noPenaltyForMissing: event.target.checked }).then(() => {
                if (mountedRef.current) getEventCandidates();
            });
        }

        return (
            <TableCell className={classes.actionCell}>
                <input
                    type="checkbox"
                    checked={!!noPenaltyForMissing}
                    onChange={handleToggle}
                    title="No penalty if this event is missed"
                />
            </TableCell>
        )
    }, [getEventCandidates, mountedRef, classes]);

    const renderRows = useMemo(() => {
        if (filteredEventCandidates) {
            return filteredEventCandidates.map((event: any) => {
                const duration = event.eventEnd ? event.eventEnd - event.eventStart : null;

                let durationString = "";
                if (duration) {
                    const hours = Math.floor(duration / 3600);
                    const minutes = Math.floor((duration % 3600) / 60);
                    durationString = `${hours}h ${minutes}m`;
                }

                const coreID = <a href={`/rheem/${event.coreID}`}>{event.coreID}</a>;
                const mac = <a href={`/rheem/${event.coreID}`}>{event.mac}</a>;

                return (
                    <TableRow key={event.id}>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>{event.eventType}</TableCell>
                        <EditableCell value={event.eventSubType} onBlur={(newValue) => ArtApi.patchRheemEventCandidate(event.id, { eventSubType: newValue }).then(getEventCandidates)} />
                        <EditableCell className={classes.wrapCell} value={event.eventDescription} onBlur={(newValue) => ArtApi.patchRheemEventCandidate(event.id, { eventDescription: newValue }).then(getEventCandidates)} multiline />
                        <TableCell>{mac}</TableCell>
                        <TableCell>{coreID}</TableCell>
                        <TableCell className={classes.dateCell}>{event.eventStart ? formatUTC(event.eventStart) : ""}</TableCell>
                        <TableCell className={classes.dateCell}>{event.eventEnd ? formatUTC(event.eventEnd) : ""}</TableCell>
                        <TableCell>{durationString}</TableCell>
                        <EditableDateCell value={event.suspectedRealStart} onBlur={(newValue) => ArtApi.patchRheemEventCandidate(event.id, { suspectedRealStart: newValue }).then(getEventCandidates)} />
                        <EditableDateCell value={event.suspectedRealEnd} onBlur={(newValue) => ArtApi.patchRheemEventCandidate(event.id, { suspectedRealEnd: newValue }).then(getEventCandidates)} />
                        {renderActionButtons(event.id, "startStatus", event.startStatus)}
                        {renderActionButtons(event.id, "endStatus", event.endStatus)}
                        <EditableCell className={classes.statusExplanationCell} value={event.statusExplanation} onBlur={(newValue) => ArtApi.patchRheemEventCandidate(event.id, { statusExplanation: newValue }).then(getEventCandidates)} multiline />
                        {renderNoPenaltyCheckbox(event.id, event.noPenaltyForMissing)}
                        {renderArchiveButton(event.id, event.archived)}
                        {renderDeleteButton(event.id)}
                    </TableRow>
                )
            })
        }
    }, [filteredEventCandidates, getEventCandidates, renderActionButtons, classes.dateCell, classes.statusExplanationCell, classes.wrapCell, renderNoPenaltyCheckbox, renderArchiveButton, renderDeleteButton]);


    const renderCell = useCallback((label, key, sortable=true) => {
        const style = {
            cursor: 'pointer',
            color: 'lightblue',
        }

        const renderSortIcon = (column: string) => {
            if (sort === column) {
                return <span>&#x25B2;</span>
            } else if (sort === `-${column}`) {
                return <span>&#x25BC;</span>
            }
            return null;
        }

        const handleHeaderClick = (column: string) => {
            if (sort === column) {
                setSort(`-${column}`);
            } else {
                setSort(column);
            }

            // reset page to 1
            setPage(1);
        }

        if (sortable) {
            return (
                <TableCell onClick={() => handleHeaderClick(key)} style={style}>
                    {label} {renderSortIcon(key)}
                </TableCell>
            )
        } else {
            return <TableCell>{label}</TableCell>
        }
    }, [sort]);

    const renderPaginationRow = useMemo(() => {
        if (totalPages === 0) {
            return null;
        }

        let prevArrow;
        if (page === 1) {
            prevArrow = <span style={{ color: 'lightgrey', fontSize: '2em' }}>&#x25C0;</span>;
        } else {
            prevArrow = <span style={{ cursor: 'pointer', fontSize: '2em' }}>&#x25C0;</span>;
        }

        let nextArrow;
        if (page >= totalPages) {
            nextArrow = <span style={{ color: 'lightgrey', fontSize: '2em' }}>&#x25B6;</span>;
        } else {
            nextArrow = <span style={{ cursor: 'pointer', color: 'lightblue', fontSize: '2em' }}>&#x25B6;</span>;
        }

        return (
            <TableRow>
                <TableCell colSpan={7}>
                    <span onClick={() => setPage(page - 1)}>{prevArrow}</span>
                    <span style={{ fontSize: '2em' }}> {page} / {totalPages} </span>
                    <span onClick={() => setPage(page + 1)}>{nextArrow}</span>
                    <span> {total} results</span>
                </TableCell>
            </TableRow>
        )
    }, [totalPages, page, total]);

    const renderExportButton = useCallback(() => {
        return (
            <TableCell>
            <Button onClick={() => {
                setIsExporting(true);

                ArtApi.getRheemEventCandidates(sort, 1, filters.eventType, total, props.coreId, committedSearchTerm, includeArchived).then(response => {
                    if (!mountedRef.current) return;
                    const allEventCandidates = response.response.events;
                    let csv = "data:text/csv;charset=utf-8,";
                    csv += buildCsvRow(Object.keys(eventCandidatesTableColumns));
                    allEventCandidates.forEach((event: any) => {
                        const duration = event.eventEnd ? event.eventEnd - event.eventStart : null;
                        const durationString = duration ? `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m` : "";
                        
                        const rowValues = Object.values(eventCandidatesTableColumns).map(key => {
                            if (key === "duration") {
                                return durationString;
                            }
                            if (key === "noPenaltyForMissing") {
                                return !!event[key];
                            }
                            if (key === "eventStart" || key === "eventEnd" || key === "suspectedRealStart" || key === "suspectedRealEnd") {
                                return event[key] ? formatUTC(event[key]) : "";
                            }
                            if (key === "durationExcel" || key === "suspectedDurationExcel") {
                                // Calculate duration in seconds
                                let durationInSeconds: number | null = null;
                                
                                if (key === "durationExcel") {
                                    durationInSeconds = event.eventEnd && event.eventStart 
                                        ? event.eventEnd - event.eventStart 
                                        : null;
                                } else if (key === "suspectedDurationExcel") {
                                    durationInSeconds = event.suspectedRealEnd && event.suspectedRealStart 
                                        ? event.suspectedRealEnd - event.suspectedRealStart 
                                        : null;
                                }
                                
                                if (durationInSeconds === null) {
                                    return "";
                                }
                                
                                // Convert seconds to Excel time (fractional days)
                                // 1 day = 86400 seconds
                                return durationInSeconds / 86400;
                            }
                            if(key.endsWith("Excel")) {
                                const baseKey = key.replace("Excel", "");
                                const timestamp = event[baseKey];
                                
                                if(timestamp === null || timestamp === undefined) {
                                    return "";
                                }
                                
                                // Convert Unix timestamp (seconds) to JavaScript Date (milliseconds)
                                const date = new Date(timestamp * 1000);
                                return convertTimeToExcelTime(date);
                            }

                            return event[key] !== null && event[key] !== undefined ? event[key] : "";
                            
                        });
                        csv += buildCsvRow(rowValues);
                    });

                    const encodedUri = encodeURI(csv);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", "rheem_event_candidates.csv");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setIsExporting(false);
                });
            }} variant="outlined" disabled={isExporting}>{isExporting ? "Exporting..." : "Export"}</Button>
            </TableCell>
        )
    }, [filters.eventType, total, isExporting, sort, props.coreId, committedSearchTerm, includeArchived, eventCandidatesTableColumns]);

    return (
        <Fragment>
            {isLoading && <CircularProgress color="secondary" />}
            <ErrorSnackbar message={error} open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />

            {!isLoading && eventCandidates &&
                <Fragment>
                <Grid container spacing={3} alignItems="center">
                    <SearchBar
                        placeholder="Search"
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                        onSearch={doSearch}
                        onClearSearch={clearSearch}
                    />
                    <Grid item className={classes.checkboxContainer}>
                        <label className={classes.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={includeArchived}
                                onChange={(e) => setIncludeArchived(e.target.checked)}
                                className={classes.checkbox}
                            />
                            Show Archived Events
                        </label>
                    </Grid>
                    <MultiSelectFilter
                        fields={[
                            {
                                fieldKey: 'eventType',
                                label: 'Event Type',
                                items: eventTypes,
                                excludeItems: filters.eventType || []
                            },
                            {
                                fieldKey: 'mlDecision',
                                label: 'ML Decision',
                                items: mlDecisionOptions,
                                excludeItems: filters.mlDecision || []
                            }
                        ]}
                        onFiltersChange={setFilters}
                        label="Filter Events"
                    />
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <div className={classes.scrollWrapper}>
                            <div ref={topScrollRef} className={`${classes.topScrollbar} custom-scrollbar`}>
                                <div ref={topScrollInnerRef} className={classes.topScrollbarInner}></div>
                            </div>
                            <Paper ref={bottomScrollRef} className={classes.tableContainer}>
                                <Table className={classes.table}>
                                    <TableBody>
                                        <TableRow>
                                            {renderExportButton()}
                                        </TableRow>
                                        <TableRow>
                                            {renderCell("Event Type", "eventType")}
                                            {renderCell("Event SubType", "eventSubType")}
                                            {renderCell("Event Description", "eventDescription")}
                                            {renderCell("MAC Address", "mac")}
                                            {renderCell("Core ID", "coreID")}
                                            {renderCell("Start Time", "eventStart")}
                                            {renderCell("End Time", "eventEnd")}
                                            {renderCell("Duration", "duration")}
                                            {renderCell("Suspected Real Start", "suspectedRealStart")}
                                            {renderCell("Suspected Real End", "suspectedRealEnd")}
                                            {renderCell("Start Status", "startStatus")}
                                            {renderCell("End Status", "endStatus")}
                                            {renderCell("Status Explanation", "statusExplanation")}
                                            {renderCell("No Penalty For Missing", "noPenaltyForMissing", false)}
                                            {renderCell("Archived", "archived", false)}
                                            <TableCell></TableCell>
                                        </TableRow>
                                        {renderRows}
                                        {renderPaginationRow}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </div>
                    </Grid>
                </Grid>
                </Fragment>
            }
        </Fragment>
    )
}
